import PropTypes from 'prop-types';
import { TrashIcon } from '@heroicons/react/24/solid';
import { ScaleIcon } from '@heroicons/react/24/solid';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { pathwayActions } from '@/stores/pathwayStore';
import { caseActions } from '@/stores/caseStore';
import featureConfig from '@/featureConfig';

const EdgePopover = ({
  name,
  typeName,
  unit,
  label,
  anchorable,
  edgeId,
  sourceId,
  sourceHandle,
  targetHandle,
  children,
  isOpen,
  onToggle,
  onClose,
}) => {
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: open => {
      if (open) {
        onToggle();
      } else {
        onClose();
      }
    },
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
    ],
  });

  const disconnectInputFromOutput = () => {
    pathwayActions.disconnectInputFromOutput({
      id: edgeId,
      sourceHandle,
      targetHandle,
    });
  };

  const openAnchorForm = () => {
    caseActions.setTempAnchor({
      id: sourceId,
      name,
      unit,
      label,
      handle: sourceHandle,
      handleType: 'output',
      typeName,
    });
  };

  const openCustomPropertyForm = () => {
    caseActions.setTempCustomProperty({
      id: sourceId,
      name,
      label,
      handle: sourceHandle,
      handleType: 'output',
      typeName,
      unit,
    });
    onClose();
  };

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: onToggle,
        })}
      >
        {children}
      </button>
      {isOpen && (
        <ul
          // eslint-disable-next-line react-hooks/refs
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
          className="z-[1002] dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52"
        >
          {featureConfig.manageModules && (
            <li>
              <button onClick={disconnectInputFromOutput}>
                <TrashIcon className="w-5 h-5" />
                Remove Connection
              </button>
            </li>
          )}
          {anchorable && (
            <li>
              <button onClick={openAnchorForm}>
                <ScaleIcon className="w-5 h-5" />
                Set as Basis
              </button>
            </li>
          )}
          <li>
            <button onClick={openCustomPropertyForm}>
              <Cog6ToothIcon className="w-5 h-5" />
              Set Custom Property
            </button>
          </li>
        </ul>
      )}
    </>
  );
};

EdgePopover.propTypes = {
  edgeId: PropTypes.string,
  name: PropTypes.string,
  unit: PropTypes.object,
  label: PropTypes.string,
  sourceHandle: PropTypes.string,
  targetHandle: PropTypes.string,
  sourceId: PropTypes.string,
  anchorable: PropTypes.bool,
  typeName: PropTypes.string,
  children: PropTypes.node,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EdgePopover;
